import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Card,
  Input,
  Button,
  Typography,
  Space,
  Tag,
  Switch,
  Row,
  Col,
  Tooltip,
  message,
} from "antd";
import { useNavigate } from "react-router-dom";
import { useGlobal } from "../../contexts/GlobalContext";
import { useCurrentProject } from "../../hooks/useCurrentProject";
import request from "../../utils/request";
import { projectPath } from "../../config/nav";
import type { ProjectUsageOverview } from "./projectUsageModel";

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const PROMPT_VARS = ["{title}", "{content}", "{lang}", "{url}", "{site_name}"];

type CapabilityKey =
  | "chat"
  | "search"
  | "summary"
  | "hotwords"
  | "podcast"
  | "recommendations"
  | "copilot";

type Capability = {
  key: CapabilityKey;
  label: string;
  icon: string;
  desc: string;
  // 副作用/预植入类：说明其授权受后端策略/套餐约束。
  kind: "core" | "pre-injected" | "side-effect";
};

const CAPABILITIES: Capability[] = [
  { key: "chat", label: "对话", icon: "ri-message-3-line", desc: "站内知识问答（RAG），挂件核心能力。", kind: "core" },
  { key: "search", label: "搜索", icon: "ri-search-line", desc: "站内内容搜索。", kind: "core" },
  { key: "summary", label: "摘要", icon: "ri-file-text-line", desc: "文章 AI 摘要，预植入到页面。已生成的会员期内免费复用。", kind: "pre-injected" },
  { key: "hotwords", label: "热词", icon: "ri-hashtag", desc: "文章热词，预植入到页面。", kind: "pre-injected" },
  { key: "podcast", label: "AI 播客", icon: "ri-mic-line", desc: "文章转 AI 播客音频。", kind: "pre-injected" },
  { key: "recommendations", label: "AI 推荐", icon: "ri-thumb-up-line", desc: "相关内容 AI 推荐。", kind: "pre-injected" },
  { key: "copilot", label: "Copilot", icon: "ri-robot-2-line", desc: "自动执行站内动作、跳转页面（高危操作需访客确认）。", kind: "side-effect" },
];

const formatNumber = (value: number) => value.toLocaleString("zh-CN");

// 能力页展示键 → 后端持久开关键（config.features / runtime-config 消费键）。
// search 为核心能力、copilot 由站点操作策略治理，均无持久开关，保持展示只读。
const BACKEND_FEATURE_KEY: Partial<Record<CapabilityKey, string>> = {
  chat: "chat",
  summary: "summary",
  hotwords: "hotwords",
  podcast: "podcast",
  recommendations: "recommend",
};

type FeatureState = { key: string; requested: boolean; allowed: boolean; effective: boolean };

const ProjectSummarySettings: React.FC = () => {
  const navigate = useNavigate();
  const { API_BASE_URL, refreshProjects } = useGlobal();
  const { project: routeProject, projectKey } = useCurrentProject();
  const pid = routeProject?.id;

  const [prompt, setPrompt] = useState("");
  const [savingPrompt, setSavingPrompt] = useState(false);
  const [usage, setUsage] = useState<ProjectUsageOverview | null>(null);
  const [features, setFeatures] = useState<Record<string, FeatureState>>({});
  const [savingFeature, setSavingFeature] = useState<string | null>(null);

  const loadFeatures = useCallback(async (signal?: AbortSignal) => {
    if (!pid) return;
    try {
      const res = await request.get<{ code: number; data: { features: FeatureState[] } }>(
        `${API_BASE_URL}/api/v3/projects/${pid}/features`,
        { signal }
      );
      if (res.data.code === 200) {
        const map: Record<string, FeatureState> = {};
        for (const f of res.data.data.features || []) map[f.key] = f;
        setFeatures(map);
      }
    } catch (_) {
      /* 功能开关读取失败不阻断能力页展示 */
    }
  }, [API_BASE_URL, pid]);

  const toggleFeature = async (backendKey: string, next: boolean) => {
    if (!pid) return;
    setSavingFeature(backendKey);
    try {
      const res = await request.put<{ code: number; msg?: string; data: { features: FeatureState[] } }>(
        `${API_BASE_URL}/api/v3/projects/${pid}/features`,
        { features: { [backendKey]: next } }
      );
      if (res.data.code === 200) {
        const map: Record<string, FeatureState> = {};
        for (const f of res.data.data.features || []) map[f.key] = f;
        setFeatures(map);
      } else {
        message.error(res.data.msg || "保存失败");
      }
    } catch (e: any) {
      message.error(e?.response?.data?.msg || "保存失败");
    } finally {
      setSavingFeature(null);
    }
  };

  useEffect(() => {
    setPrompt(routeProject?.prompt || "");
  }, [routeProject?.prompt]);

  const loadUsage = useCallback(async (signal?: AbortSignal) => {
    if (!pid) return;
    try {
      const res = await request.get<{ code: number; data: ProjectUsageOverview }>(
        `${API_BASE_URL}/api/v3/user/project-usage`,
        { params: { project_id: pid }, signal }
      );
      if (res.data.code === 200) setUsage(res.data.data);
    } catch (_) {
      /* 用量读取失败不阻断能力页展示 */
    }
  }, [API_BASE_URL, pid]);

  useEffect(() => {
    const controller = new AbortController();
    loadUsage(controller.signal);
    loadFeatures(controller.signal);
    return () => controller.abort();
  }, [loadUsage, loadFeatures]);

  const savePrompt = async () => {
    if (!pid) return;
    setSavingPrompt(true);
    try {
      const res = await request.put(`${API_BASE_URL}/api/v3/projects/${pid}/prompt`, { prompt });
      if (res.data.code === 200) {
        message.success("摘要提示词已保存");
        await refreshProjects();
      } else {
        message.error(res.data.msg || "保存失败");
      }
    } catch (e: any) {
      message.error(e?.response?.data?.msg || "保存失败");
    } finally {
      setSavingPrompt(false);
    }
  };

  // 有效开关目前由：接入片段 data-features（前端）+ 套餐/项目类型（后端）共同决定。
  // 每项目持久化功能开关的后端端点尚在接入中，故此处 Switch 先只读展示当前有效态。
  const isFullProduct = Boolean(routeProject?.is_postchat);
  const effectiveEnabled = useCallback(
    (cap: Capability): boolean => {
      if (cap.kind === "pre-injected") return cap.key === "summary" ? true : isFullProduct;
      return isFullProduct; // chat/search/copilot 走全功能项目
    },
    [isFullProduct]
  );

  const usageLine = useMemo(() => {
    const summaryTokens = usage?.summary?.actualTokens ?? 0;
    const summaryReq = usage?.summary?.requestCount ?? 0;
    const modelReq = usage?.modelTotals?.requestCount ?? 0;
    return { summaryTokens, summaryReq, modelReq };
  }, [usage]);

  if (!routeProject) return null;

  const goInstall = () => projectKey && navigate(projectPath(String(projectKey), "website"));
  const goUsage = () => projectKey && navigate(projectPath(String(projectKey), "usage"));

  return (
    <div style={{ padding: 24, maxWidth: 1080, margin: "0 auto" }}>
      <Space align="center" style={{ marginBottom: 4 }}>
        <Title level={3} style={{ margin: 0 }}>能力</Title>
        <Tag color={isFullProduct ? "blue" : "green"}>{isFullProduct ? "全功能" : "仅摘要"}</Tag>
      </Space>
      <Paragraph type="secondary" style={{ marginTop: 0 }}>
        按功能查看开关、用量与配额。开关体现在「接入」代码片段并受套餐约束；已生成的摘要/热词/播客在会员期内免费复用，新生成消耗生成配额。
      </Paragraph>

      <Row gutter={[16, 16]}>
        {CAPABILITIES.map((cap) => {
          const enabled = effectiveEnabled(cap);
          const isSummary = cap.key === "summary";
          const isChat = cap.key === "chat";
          return (
            <Col xs={24} md={12} key={cap.key}>
              <Card
                size="small"
                title={
                  <Space>
                    <i className={cap.icon} style={{ color: "#3979F6" }} />
                    <span>{cap.label}</span>
                    {cap.kind === "side-effect" && <Tag color="gold">副作用</Tag>}
                    {cap.kind === "pre-injected" && <Tag>预植入</Tag>}
                  </Space>
                }
                extra={(() => {
                  const backendKey = BACKEND_FEATURE_KEY[cap.key];
                  if (!backendKey) {
                    return (
                      <Tooltip title={cap.key === "copilot" ? "由站点操作策略治理，非此处开关。" : "核心能力，无需开关。"}>
                        <Switch checked={enabled} disabled size="small" />
                      </Tooltip>
                    );
                  }
                  const state = features[backendKey];
                  const allowed = state?.allowed ?? enabled;
                  const checked = state ? state.requested : enabled;
                  return (
                    <Tooltip title={allowed ? "开启后由前端片段加载并受套餐约束" : "当前项目类型/套餐不支持"}>
                      <Switch
                        checked={checked}
                        disabled={!allowed}
                        loading={savingFeature === backendKey}
                        size="small"
                        onChange={(value) => toggleFeature(backendKey, value)}
                      />
                    </Tooltip>
                  );
                })()}
              >
                <Paragraph type="secondary" style={{ marginTop: 0, minHeight: 40 }}>{cap.desc}</Paragraph>

                <Row gutter={8}>
                  <Col span={12}>
                    <Text type="secondary" style={{ fontSize: 12 }}>本月用量</Text>
                    <div style={{ fontSize: 18, marginTop: 2 }}>
                      {isSummary ? (
                        <span>{formatNumber(usageLine.summaryTokens)} <Text type="secondary" style={{ fontSize: 12 }}>Token</Text></span>
                      ) : isChat ? (
                        <span>{formatNumber(usageLine.modelReq)} <Text type="secondary" style={{ fontSize: 12 }}>次</Text></span>
                      ) : (
                        <Text type="secondary" style={{ fontSize: 14 }}>待接入</Text>
                      )}
                    </div>
                  </Col>
                  <Col span={12}>
                    <Text type="secondary" style={{ fontSize: 12 }}>周/月配额</Text>
                    <div style={{ fontSize: 18, marginTop: 2 }}>
                      <Text type="secondary" style={{ fontSize: 14 }}>配置中</Text>
                    </div>
                  </Col>
                </Row>

                <Space style={{ marginTop: 8 }} wrap>
                  <Button size="small" onClick={goInstall}>去接入</Button>
                  <Button size="small" onClick={goUsage}>用量与扩容</Button>
                  {isSummary && (
                    <Text type="secondary" style={{ fontSize: 12 }}>提示词见下方</Text>
                  )}
                </Space>
              </Card>
            </Col>
          );
        })}
      </Row>

      <Card title="摘要提示词" style={{ marginTop: 16 }}>
        <Paragraph type="secondary" style={{ marginTop: 0 }}>
          作为本项目所有摘要的基底提示词，即时生效。可用变量：
          {PROMPT_VARS.map((v) => (
            <Text key={v} code style={{ marginLeft: 6 }}>{v}</Text>
          ))}
        </Paragraph>
        <TextArea
          rows={6}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="留空则使用平台默认提示词。例如：请用 {lang} 为《{title}》生成 150 字以内的摘要…"
        />
        <div style={{ marginTop: 12 }}>
          <Button type="primary" loading={savingPrompt} onClick={savePrompt}>保存提示词</Button>
        </div>
      </Card>

      <Paragraph type="secondary" style={{ marginTop: 12 }}>
        子 Key 管理已移至各智能体的「Agent 配置 → 子 Key」区块，可在智能体列表中进入对应智能体配置页管理。
      </Paragraph>
    </div>
  );
};

export default ProjectSummarySettings;
